<template>
  <AddDeviceButton
    v-if="isPlaceholder || !isConnected"
    class="opacity-50 pointer-events-none"
    :label="device"
    :icon="device"
    @click="handleStethoscopeConnection"
  />
  <template v-else>
    <AddDeviceButton
      v-if="isConnected && !audioStream && !loading"
      :class="{ 'opacity-50 pointer-events-none': !isConnected }"
      :label="device"
      :icon="device"
      @click="handleStethoscopeConnection"
    />

    <div class="relative w-full h-0 pt-55" v-if="(isConnected && audioStream) || loading">
      <div class="absolute h-full w-full top-0 left-0">
        <!-- Stethoscope vizualizer instead of video -->
        <StethoscopeAudioVisualizer v-if="audioStream" :stream="audioStream" :prePhase="true" />
        <ViceLoader class="top-0" v-if="loading" />
        <button
          class="block right-0 top-0 absolute z-20"
          :title="`Stop ${device} Stream`"
          @click="handleStethoscopeConnection"
        >
          <TrashIcon class="h-6 w-6 shadow-filter" />
        </button>
        <span class="text-sm label-text">{{ device }}</span>
      </div>
    </div>
  </template>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue';
import { TrashIcon } from '@heroicons/vue/solid';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import { STETHOSCOPE_LABELS, EXTERNAL_CONNECTED_AUDIO_DEVICES } from '@/Utils/vice-constants';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';

import useSessionStore from '@/Stores/useSessionStore';
import AddDeviceButton from '../Buttons/AddDeviceButton.vue';

import StethoscopeAudioVisualizer from '@/Components/Session/Grid/StethoscopeAudioVisualizer.vue';

export default {
  name: 'PreConsultationExternalAudioDevicePreview',
  props: {
    device: {
      type: String,
      required: true,
    },
  },
  components: {
    AddDeviceButton,
    ViceLoader,
    TrashIcon,
    StethoscopeAudioVisualizer,
  },
  setup(_props) {
    const isPlaceholder = computed(() => _props.device === 'Medical Device');
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const sessionStore = useSessionStore();
    const audioStream = ref(null);
    const loading = ref(false);

    let deviceLabels = [];
    switch (_props.device) {
      case EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE:
        deviceLabels = STETHOSCOPE_LABELS;
        break;
      default:
    }

    const isCurrentlyStreaming = computed(() =>
      Boolean(
        multiCameraDevicesStore.allExternalConnectedAudioDevices.find(
          (deviceName) => deviceName === _props.device,
        ),
      ),
    );

    const isConnected = computed(() =>
      Boolean(
        multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.AUDIO.Microphone.find((_device) =>
          deviceLabels.some((singleLabel) => _device.label.includes(singleLabel)),
        ),
      ),
    );

    const devicePredefined = computed(() =>
      multiCameraDevicesStore.clinicianDevicesForSession.clinician_devices.some(
        (device) => device.device_type_label === _props.device,
      ),
    );

    const deviceCached = computed(
      () =>
        !!multiCameraDevicesStore.cachedExternalAudioDevices.find(
          (device) => device.name === _props.device,
        ),
    );

    const mediaDeviceId = computed(() => {
      let deviceId = null;

      if (devicePredefined.value) {
        // The case when device should be connected from clinician's 'use case'
        const device = multiCameraDevicesStore.clinicianDevicesForSession.clinician_devices.find(
          (_device) => _device.device_type_label === _props.device,
        );

        if (device) {
          deviceId = device.device_data.deviceId;
        }
      } else {
        // The case when device should be connected directly from session
        const device = multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.AUDIO.Microphone.find(
          (_device) => deviceLabels.some((singleLabel) => _device.label.includes(singleLabel)),
        );

        if (device) {
          deviceId = device.deviceId;
        }
      }

      return deviceId;
    });

    watch(isConnected, async (newVal) => {
      if (newVal) {
        await addCachedDevice();
      } else {
        audioStream.value = null;
        await multiCameraDevicesStore.removeExternalAudioDevice(_props.device, false);
        loading.value = false;
      }
    });

    const handleStethoscopeConnection = async () => {
      try {
        loading.value = true;
        if (isCurrentlyStreaming.value) {
          await multiCameraDevicesStore.removeExternalAudioDevice(_props.device);
          audioStream.value = null;
        } else {
          await multiCameraDevicesStore.addExternalAudioDevice(
            sessionStore.sessionId,
            _props.device,
            deviceLabels,
          );

          await startAudioStream(mediaDeviceId.value);
        }
        multiCameraDevicesStore.markSessionSetupAsChanged();
      } catch (error) {
        console.error({ error });
      } finally {
        loading.value = false;
      }
    };

    const startAudioStream = async (deviceId) => {
      try {
        const constraints = {
          audio: {
            deviceId: deviceId ? { exact: deviceId } : undefined,
          },
        };

        audioStream.value = await navigator.mediaDevices.getUserMedia(constraints);

        loading.value = false;
      } catch (error) {
        console.error('Error accessing camera:', error);
      }
    };

    const addCachedDevice = async () => {
      if (isConnected.value && deviceCached.value) {
        loading.value = true;

        await multiCameraDevicesStore.addExternalAudioDevice(
          sessionStore.sessionId,
          _props.device,
          deviceLabels,
        );

        await startAudioStream(mediaDeviceId.value);
        loading.value = false;
      }
    };

    onMounted(async () => {
      const isDeviceSetupChanged = multiCameraDevicesStore.isSessionSetupWasChanged;
      // Remove from cache if setup not changed and device was not predefined
      if (!isDeviceSetupChanged && !devicePredefined.value) {
        multiCameraDevicesStore.removeExternalAudioDeviceFromCache(_props.device);
      }

      // Add to cache and start if setup not changed and device predefined
      if (!isDeviceSetupChanged && devicePredefined.value) {
        multiCameraDevicesStore.addExternalAudioDeviceToCache(_props.device, deviceLabels);
        await addCachedDevice();
      }

      // Start device from cache if setup was changed
      if (isDeviceSetupChanged) await addCachedDevice();

      setTimeout(() => {
        console.log('mounded', _props.device, isCurrentlyStreaming.value);
      }, 3000);
    });

    return {
      isPlaceholder,
      loading,
      isConnected,
      multiCameraDevicesStore,
      handleStethoscopeConnection,
      audioStream,
    };
  },
};
</script>
<style scoped>
.pt-55 {
  padding-top: 55.59%;
}
.label-text {
  display: block;
  position: absolute;
  bottom: 0;
  left: 4px;
  z-index: 10;
}
</style>
